import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAccess } from '@/contexts/AccessContext';
import { useTranslation } from 'react-i18next';
import { LegalFooterCompact } from '@/components/legal/LegalFooter';
import { Eye, EyeOff, LogIn } from 'lucide-react';

function LoginV2() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { refreshAccessLevel } = useAccess();
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(loginData.email, loginData.password);
      
      if (result.success) {
        // Refresh access level after successful login
        refreshAccessLevel();
        
        // Check if onboarding is needed
        try {
          const token = localStorage.getItem('systemklart_token');
          const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v2/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const userData = await meRes.json();
          if (userData.onboarding_completed === false && userData.role !== 'SystemAdmin') {
            navigate('/onboarding');
            return;
          }
        } catch (e) { /* proceed to dashboard */ }
        
        navigate('/dashboard');
      } else {
        setError(result.error || (isNorwegian ? 'Innlogging feilet' : 'Login failed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(isNorwegian ? 'En feil oppstod under innlogging' : 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    i18n.changeLanguage(isNorwegian ? 'en' : 'nb');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleLanguage}
                className="px-3 py-1.5 text-sm text-slate-400 hover:text-white border border-slate-700 rounded-lg hover:border-slate-600 transition-colors"
              >
                {isNorwegian ? 'EN' : 'NO'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">
              {isNorwegian ? 'Logg inn' : 'Sign in'}
            </h1>
            <p className="text-slate-400">
              {isNorwegian ? 'Få kontroll på internkontroll' : 'Take control of your compliance'}
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  {isNorwegian ? 'E-postadresse' : 'Email address'}
                </label>
                <input
                  type="email"
                  id="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
                  placeholder={isNorwegian ? 'din@epost.no' : 'your@email.com'}
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                  {isNorwegian ? 'Passord' : 'Password'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all pr-12"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/50" />
                  {isNorwegian ? 'Husk meg' : 'Remember me'}
                </label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  {isNorwegian ? 'Glemt passord?' : 'Forgot password?'}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {loading ? (
                  <span>{isNorwegian ? 'Logger inn...' : 'Signing in...'}</span>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>{isNorwegian ? 'Logg inn' : 'Sign in'}</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-400 text-sm">
                {isNorwegian ? 'Har du ikke konto?' : "Don't have an account?"}{' '}
                <Link
                  to="/register"
                  className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  data-testid="register-link"
                >
                  {isNorwegian ? 'Registrer deg her' : 'Register here'}
                </Link>
              </p>
            </div>
          </div>

          {/* System Admin Login Link */}
          <div className="mt-6 text-center">
            <Link
              to="/system-login"
              className="text-slate-500 hover:text-slate-400 text-sm transition-colors"
              data-testid="system-login-link"
            >
              {isNorwegian ? 'Systemadministrator' : 'System Administrator'}
            </Link>
          </div>
        </div>
      </div>

      {/* Legal Footer */}
      <LegalFooterCompact />
    </div>
  );
}

export default LoginV2;
